import React, { useState } from 'react';
import { auth, db } from '../../config/firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import logo from '../../logo.png';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';

const COUNTRIES = [
  { name: 'Tanzania', code: '+255', flag: '🇹🇿' },
  { name: 'Kenya', code: '+254', flag: '🇰🇪' },
  { name: 'Uganda', code: '+256', flag: '🇺🇬' },
  { name: 'Rwanda', code: '+250', flag: '🇷🇼' },
  { name: 'Burundi', code: '+257', flag: '🇧🇮' },
  { name: 'Ethiopia', code: '+251', flag: '🇪🇹' },
  { name: 'South Africa', code: '+27', flag: '🇿🇦' },
  { name: 'Nigeria', code: '+234', flag: '🇳🇬' },
  { name: 'Ghana', code: '+233', flag: '🇬🇭' },
  { name: 'USA', code: '+1', flag: '🇺🇸' },
  { name: 'UK', code: '+44', flag: '🇬🇧' },
  { name: 'Germany', code: '+49', flag: '🇩🇪' },
  { name: 'France', code: '+33', flag: '🇫🇷' },
  { name: 'Canada', code: '+1', flag: '🇨🇦' },
  { name: 'Australia', code: '+61', flag: '🇦🇺' },
  { name: 'India', code: '+91', flag: '🇮🇳' },
  { name: 'China', code: '+86', flag: '🇨🇳' },
  { name: 'UAE', code: '+971', flag: '🇦🇪' },
  { name: 'Saudi Arabia', code: '+966', flag: '🇸🇦' },
  { name: 'Netherlands', code: '+31', flag: '🇳🇱' },
];

const EMAIL_SUGGESTIONS = [
  '@gmail.com',
  '@yahoo.com',
  '@outlook.com',
  '@hotmail.com',
  '@icloud.com',
];

export default function SignUp() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    country: 'Tanzania',
    organization: '',
    reason: '',
    howDidYouHear: '',
  });

  const [errors, setErrors] = useState({});

  function handleChange(e) {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }

    if (name === 'email') {
      const atIndex = value.indexOf('@');
      if (atIndex > 0 && !value.includes('.', atIndex)) {
        const typed = value.substring(atIndex);
        const suggestions = EMAIL_SUGGESTIONS
          .filter(s => s.startsWith(typed) && s !== typed)
          .map(s => value.substring(0, atIndex) + s);
        setEmailSuggestions(suggestions);
      } else if (atIndex === -1) {
        setEmailSuggestions([]);
      } else {
        setEmailSuggestions([]);
      }
    }

    if (name === 'phone') {
      const cleaned = value.replace(/[^0-9]/g, '');
      setForm(prev => ({ ...prev, phone: cleaned }));
    }
  }

  function selectEmail(suggestion) {
    setForm({ ...form, email: suggestion });
    setEmailSuggestions([]);
  }

  function checkPasswordStrength(password) {
    if (password.length < 8) return {
      strength: 'Weak', color: '#C62828', width: '33%'
    };
    if (
      password.match(/[A-Z]/) &&
      password.match(/[0-9]/) &&
      password.match(/[^A-Za-z0-9]/)
    ) return {
      strength: 'Strong', color: '#14452F', width: '100%'
    };
    return {
      strength: 'Medium', color: '#E65100', width: '66%'
    };
  }

  function validateStep1() {
    const newErrors = {};
    if (!form.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (form.phone.length < 9) {
      newErrors.phone = 'Phone number is too short';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function validateStep2() {
    const newErrors = {};
    if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!form.password.match(/[A-Z]/)) {
      newErrors.password = 'Password must have at least one capital letter';
    } else if (!form.password.match(/[0-9]/)) {
      newErrors.password = 'Password must have at least one number';
    }
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!form.reason.trim()) {
      newErrors.reason = 'Please tell us why you want to support us';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleNext() {
    if (validateStep1()) setStep(2);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateStep2()) return;

    setLoading(true);
    setError('');

    try {
      const result = await createUserWithEmailAndPassword(
        auth, form.email, form.password
      );
      await sendEmailVerification(result.user);
      await setDoc(doc(db, 'users', result.user.uid), {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: `${selectedCountry.code}${form.phone}`,
        country: form.country || selectedCountry.name,
        organization: form.organization,
        reason: form.reason,
        howDidYouHear: form.howDidYouHear,
        role: 'donor',
        verified: false,
        createdAt: serverTimestamp(),
      });

  setSuccess(form.email);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please login.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  }

  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.includes(countrySearch)
  );

  const passwordStrength = checkPasswordStrength(form.password);
if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successBox}>
            <div style={styles.successEmoji}></div>
            <div style={styles.successTitle}>
              Welcome to Iyanna Foundation!
            </div>
            <div style={styles.successSubtitle}>
              Account Successfully Created
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successEmail}>
              📧 {success}
            </div>
            <div style={styles.successText}>
              A verification email has been sent to your
              inbox. Please check your email including
              spam folder and verify your account.
            </div>
            <div style={styles.successSteps}>
              <div style={styles.successStep}>
                <div style={styles.successStepNum}>1</div>
                <div style={styles.successStepText}>
                  Check your email inbox
                </div>
              </div>
              <div style={styles.successStep}>
                <div style={styles.successStepNum}>2</div>
                <div style={styles.successStepText}>
                  Click the verification link
                </div>
              </div>
              <div style={styles.successStep}>
                <div style={styles.successStepNum}>3</div>
                <div style={styles.successStepText}>
                  Come back and login
                </div>
              </div>
            </div>
            <button
              style={styles.btn}
              onClick={() => navigate('/')}
            >
              Go to Login →
            </button>
            <div style={styles.successNote}>
              Didn't receive the email? Check your spam
              folder or use Forgot Password on the login page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* LOGO */}
        <div style={styles.logoRow}>
          <div style={styles.logoMark}>
            <img
              src={logo}
              alt="Iyanna Foundation"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          <div>
            <div style={styles.logoName}>
              Iyanna Foundation
            </div>
            <div style={styles.logoSub}>
              PROTECT & EDUCATE
            </div>
          </div>
        </div>

        {/* DONOR BADGE */}
        <div style={styles.donorBadge}>
          Donor Account Registration
        </div>

        {/* STEP INDICATOR */}
        <div style={styles.stepRow}>
          <div style={styles.stepItem}>
            <div style={{
              ...styles.stepCircle,
              background: step >= 1
                ? 'linear-gradient(135deg, #F57C00, #FFA726)'
                : 'rgba(255,255,255,0.1)',
            }}>
              {step > 1 ? '✓' : '1'}
            </div>
            <div style={styles.stepLabel}>Personal Info</div>
          </div>
          <div style={styles.stepLine} />
          <div style={styles.stepItem}>
            <div style={{
              ...styles.stepCircle,
              background: step >= 2
                ? 'linear-gradient(135deg, #F57C00, #FFA726)'
                : 'rgba(255,255,255,0.1)',
            }}>
              2
            </div>
            <div style={styles.stepLabel}>Security & More</div>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* STEP 1 */}
        {step === 1 && (
          <div>
            <div style={styles.heading}>Tell Us About You</div>
            <div style={styles.sub}>
              Step 1 of 2 — Personal Information
            </div>

            <div style={styles.row}>
              <div style={styles.half}>
                <div style={styles.label}>First Name *</div>
                <input
                  style={{
                    ...styles.input,
                    ...(errors.firstName ? styles.inputError : {})
                  }}
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="e.g. Sarah"
                />
                {errors.firstName && (
                  <div style={styles.fieldError}>
                    {errors.firstName}
                  </div>
                )}
              </div>
              <div style={styles.half}>
                <div style={styles.label}>Last Name *</div>
                <input
                  style={{
                    ...styles.input,
                    ...(errors.lastName ? styles.inputError : {})
                  }}
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="e.g. Mwamba"
                />
                {errors.lastName && (
                  <div style={styles.fieldError}>
                    {errors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div style={styles.label}>Email Address *</div>
            <div style={{ position: 'relative' }}>
              <input
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {})
                }}
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
                autoComplete="email"
              />
              {emailSuggestions.length > 0 && (
                <div style={styles.suggestions}>
                  {emailSuggestions.map((s, i) => (
                    <div
                      key={i}
                      style={styles.suggestionItem}
                      onClick={() => selectEmail(s)}
                    >
                      📧 {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.email && (
              <div style={styles.fieldError}>{errors.email}</div>
            )}

            <div style={styles.label}>Phone Number *</div>
            <div style={styles.phoneRow}>
              <div
                style={styles.countryCode}
                onClick={() => setShowCountryPicker(!showCountryPicker)}
              >
                <span>{selectedCountry.flag}</span>
                <span style={styles.codeText}>
                  {selectedCountry.code}
                </span>
                <span style={styles.codeArrow}>▼</span>
              </div>
              <input
                style={{
                  ...styles.phoneInput,
                  ...(errors.phone ? styles.inputError : {})
                }}
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="7XX XXX XXX"
                maxLength={12}
                inputMode="numeric"
              />
            </div>
            {errors.phone && (
              <div style={styles.fieldError}>{errors.phone}</div>
            )}

            {showCountryPicker && (
              <div style={styles.countryPicker}>
                <input
                  style={styles.countrySearch}
                  placeholder="Search country..."
                  value={countrySearch}
                  onChange={e => setCountrySearch(e.target.value)}
                  autoFocus
                />
                <div style={styles.countryList}>
                  {filteredCountries.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        ...styles.countryItem,
                        ...(selectedCountry.name === c.name
                          ? styles.countryItemActive : {})
                      }}
                      onClick={() => {
                        setSelectedCountry(c);
                        setForm({ ...form, country: c.name });
                        setShowCountryPicker(false);
                        setCountrySearch('');
                      }}
                    >
                      <span>{c.flag}</span>
                      <span style={styles.countryName}>{c.name}</span>
                      <span style={styles.countryCodeText}>{c.code}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.label}>Organization (Optional)</div>
            <input
              style={styles.input}
              name="organization"
              value={form.organization}
              onChange={handleChange}
              placeholder="Company or NGO name"
            />

            <button
              style={styles.btn}
              onClick={handleNext}
              type="button"
            >
              Continue →
            </button>

            <div style={styles.loginLink}>
              Already have an account?{' '}
              <span
                style={styles.loginLinkSpan}
                onClick={() => navigate('/')}
              >
                Sign In
              </span>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div style={styles.heading}>Almost Done!</div>
            <div style={styles.sub}>
              Step 2 of 2 — Security & More
            </div>

            <div style={styles.label}>Password *</div>
            <div style={styles.passwordRow}>
              <input
                style={{
                  ...styles.passwordInput,
                  ...(errors.password ? styles.inputError : {})
                }}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                placeholder="Min 8 characters"
              />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword
                  ? <AiOutlineEyeInvisible
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                  : <AiOutlineEye
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                }
              </button>
            </div>

            {form.password.length > 0 && (
              <div style={styles.strengthRow}>
                <div style={styles.strengthBg}>
                  <div style={{
                    ...styles.strengthFill,
                    width: passwordStrength.width,
                    background: passwordStrength.color,
                  }} />
                </div>
                <span style={{
                  ...styles.strengthLabel,
                  color: passwordStrength.color
                }}>
                  {passwordStrength.strength}
                </span>
              </div>
            )}

            <div style={styles.rules}>
              {[
                { met: form.password.length >= 8, text: 'At least 8 characters' },
                { met: !!form.password.match(/[A-Z]/), text: 'One capital letter' },
                { met: !!form.password.match(/[0-9]/), text: 'One number' },
              ].map((r, i) => (
                <div key={i} style={{
                  ...styles.rule,
                  color: r.met ? '#BBD8C4' : 'rgba(255,255,255,0.3)'
                }}>
                  {r.met ? '' : '○'} {r.text}
                </div>
              ))}
            </div>
            {errors.password && (
              <div style={styles.fieldError}>{errors.password}</div>
            )}

            <div style={styles.label}>Confirm Password *</div>
            <div style={styles.passwordRow}>
              <input
                style={{
                  ...styles.passwordInput,
                  ...(errors.confirmPassword ? styles.inputError : {})
                }}
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat your password"
              />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword
                  ? <AiOutlineEyeInvisible
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                  : <AiOutlineEye
                      size={20}
                      color="rgba(255,255,255,0.6)"
                    />
                }
              </button>
            </div>
            {errors.confirmPassword && (
              <div style={styles.fieldError}>
                {errors.confirmPassword}
              </div>
            )}

            <div style={styles.label}>
              Why do you want to support us? *
            </div>
            <textarea
              style={{
                ...styles.input,
                height: '80px',
                resize: 'vertical',
                ...(errors.reason ? styles.inputError : {})
              }}
              name="reason"
              value={form.reason}
              onChange={handleChange}
              placeholder="Tell us why you want to support Iyanna Foundation..."
            />
            {errors.reason && (
              <div style={styles.fieldError}>{errors.reason}</div>
            )}

            <div style={styles.label}>How did you hear about us?</div>
            <select
              style={styles.input}
              name="howDidYouHear"
              value={form.howDidYouHear}
              onChange={handleChange}
            >
              <option value="">Select option</option>
              <option>Social Media</option>
              <option>Friend or Family</option>
              <option>Google Search</option>
              <option>News or Media</option>
              <option>Community Event</option>
              <option>Other</option>
            </select>

            <div style={styles.btnRow}>
              <button
                type="button"
                style={styles.backBtn}
                onClick={() => setStep(1)}
              >
                ← Back
              </button>
              <button
                type="submit"
                style={{ ...styles.btn, flex: 1 }}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Account →'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #041F14 0%, #07271A 40%, #14452F 70%, #041F14 100%)',
    padding: '40px 20px',
    position: 'relative',
    overflow: 'hidden',
  },
  card: {
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '24px',
    padding: '44px 40px',
    width: '100%',
    maxWidth: '460px',
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '20px',
  },
  logoMark: {
    width: '65px',
    height: '65px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#041F14',
    padding: '0px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
    border: '2px solid rgba(255,255,255,0.15)',
  },
  logoName: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '18px',
    color: '#fff',
    lineHeight: 1.2,
    fontWeight: '700',
  },
  logoSub: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1px',
    marginTop: '2px',
  },
  donorBadge: {
    background: 'rgba(245,124,0,0.18)',
    border: '1.5px solid #F57C00',
    color: '#FFA726',
    padding: '10px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: '20px',
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '24px',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
  },
  stepCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
  },
  stepLabel: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.4)',
    whiteSpace: 'nowrap',
  },
  stepLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(255,255,255,0.15)',
    margin: '0 10px',
    marginBottom: '20px',
  },
  heading: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '24px',
    color: '#fff',
    marginBottom: '4px',
  },
  sub: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: '20px',
  },
  row: { display: 'flex', gap: '12px' },
  half: { flex: 1 },
  label: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '7px',
    marginTop: '4px',
  },
  input: {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '12px 16px',
    color: '#fff',
    fontSize: '13.5px',
    marginBottom: '4px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: 'rgba(211,47,47,0.6)',
  },
  fieldError: {
    color: '#ff8a80',
    fontSize: '11px',
    marginBottom: '10px',
    marginTop: '2px',
  },
  suggestions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#1a3d1e',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.15)',
    zIndex: 100,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  suggestionItem: {
    padding: '10px 16px',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  phoneRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '4px',
  },
  countryCode: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(255,255,255,0.07)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '12px 12px',
    cursor: 'pointer',
    flexShrink: 0,
    minWidth: '90px',
  },
  codeText: {
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
  },
  codeArrow: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '9px',
  },
  phoneInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.07)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '12px 16px',
    color: '#fff',
    fontSize: '13.5px',
    outline: 'none',
    fontFamily: 'inherit',
  },
  countryPicker: {
    background: '#1a3d1e',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.15)',
    marginBottom: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  countrySearch: {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '12px 16px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  countryList: {
    maxHeight: '180px',
    overflowY: 'auto',
  },
  countryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  countryItemActive: {
    background: 'rgba(245,124,0,0.15)',
  },
  countryName: {
    flex: 1,
    color: '#fff',
    fontSize: '13px',
  },
  countryCodeText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '12px',
  },
  strengthRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  strengthBg: {
    flex: 1,
    height: '4px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '99px',
    overflow: 'hidden',
  },
  strengthFill: {
    height: '100%',
    borderRadius: '99px',
    transition: 'width 0.3s, background 0.3s',
  },
  strengthLabel: {
    fontSize: '11px',
    fontWeight: '600',
    minWidth: '50px',
  },
  rules: {
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  rule: {
    fontSize: '11.5px',
    transition: 'color 0.2s',
  },
  error: {
    background: 'rgba(211,47,47,0.2)',
    border: '1px solid rgba(211,47,47,0.4)',
    color: '#ff8a80',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '14px',
  },
  btn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 8px 22px rgba(245,124,0,0.3)',
    fontFamily: 'inherit',
    marginTop: '8px',
    marginBottom: '12px',
  },
  btnRow: {
    display: 'flex',
    gap: '10px',
    marginTop: '8px',
  },
  backBtn: {
    padding: '14px 20px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  loginLink: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.4)',
    fontSize: '13px',
  },
  loginLinkSpan: {
    color: '#FFA726',
    cursor: 'pointer',
    fontWeight: '600',
  },
  successBox: {
    textAlign: 'center',
    padding: '20px 0',
  },
  successIcon: {
    fontSize: '56px',
    marginBottom: '16px',
  },
  successTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '24px',
    color: '#fff',
    marginBottom: '12px',
  },
  successText: {
    color: '#BBD8C4',
    fontSize: '13.5px',
    lineHeight: 1.6,
    marginBottom: '24px',
  },
  passwordRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
    width: '100%',
  },
  passwordInput: {
    width: '100%',
    background: 'rgba(255,255,255,0.07)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '12px 48px 12px 16px',
    color: '#fff',
    fontSize: '13.5px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },successEmoji: {
    fontSize: '64px',
    marginBottom: '16px',
    animation: 'pulse 1s ease-in-out',
  },
  successSubtitle: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '16px',
  },
  successDivider: {
    height: '1px',
    background: 'rgba(255,255,255,0.1)',
    marginBottom: '16px',
  },
  successEmail: {
    fontSize: '13px',
    color: '#FFA726',
    fontWeight: '600',
    marginBottom: '12px',
    padding: '10px 16px',
    background: 'rgba(245,124,0,0.1)',
    borderRadius: '10px',
    border: '1px solid rgba(245,124,0,0.2)',
  },
  successSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '24px',
    textAlign: 'left',
  },
  successStep: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  successStepNum: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#fff',
    flexShrink: 0,
  },
  successStepText: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.8)',
  },
  successNote: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.3)',
    textAlign: 'center',
    marginTop: '12px',
    lineHeight: 1.5,
  },
};